import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { firestore } from '../lib/firebase';
import type { ContentEntry, ContentEntryPayload } from '../types/content';

const contentCollection = collection(firestore, 'contentEntries');

const buildDocId = (locale: string, contentKey: string) => {
  const sanitizedLocale = locale.replace(/[^a-zA-Z0-9-]/g, '_');
  const sanitizedKey = contentKey.replace(/[^a-zA-Z0-9_.-]/g, '_');
  return `${sanitizedLocale}__${sanitizedKey}`;
};

const toContentEntry = (snapshot: QueryDocumentSnapshot<DocumentData>): ContentEntry => {
  const data = snapshot.data() as Record<string, unknown>;
  const updatedAtRaw = data.updatedAt as Timestamp | Date | undefined;
  return {
    id: snapshot.id,
    contentKey: (data.contentKey as string) ?? '',
    locale: (data.locale as string) ?? 'it',
    section: (data.section as string) ?? 'general',
    label: data.label as string | undefined,
    helper: data.helper as string | undefined,
    type: (data.type as ContentEntry['type']) ?? 'text',
    value: data.value as ContentEntry['value'],
    updatedAt:
      updatedAtRaw instanceof Timestamp
        ? updatedAtRaw.toDate()
        : updatedAtRaw instanceof Date
          ? updatedAtRaw
          : undefined,
  };
};

export const subscribeToContentEntries = (
  locale: string,
  callback: (entries: ContentEntry[]) => void,
  onError?: (error: Error) => void,
) =>
  onSnapshot(
    query(contentCollection, where('locale', '==', locale), orderBy('contentKey', 'asc')),
    (querySnapshot) => {
      const entries = querySnapshot.docs.map((docSnapshot) => toContentEntry(docSnapshot));
      callback(entries);
    },
    (error) => {
      onError?.(error as Error);
    },
  );

export const saveContentEntry = async (payload: ContentEntryPayload) => {
  const docId = buildDocId(payload.locale, payload.contentKey);
  const docRef = doc(contentCollection, docId);

  const { label, helper, ...rest } = payload;

  const data: Record<string, unknown> = {
    ...rest,
    updatedAt: serverTimestamp(),
  };

  if (label !== undefined) {
    data.label = label;
  }

  if (helper !== undefined) {
    data.helper = helper;
  }

  await setDoc(
    docRef,
    data,
    { merge: true },
  );
};

export const deleteContentEntry = async (locale: string, contentKey: string) => {
  const docId = buildDocId(locale, contentKey);
  const docRef = doc(contentCollection, docId);
  await deleteDoc(docRef);
};
